import { Link } from "react-router-dom";
import { useCallback, useContext, useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";

import { PencilIcon, DocumentTextIcon, TrashIcon, } from "@heroicons/react/24/outline";

// Context
import SnackbarContext from "../../../context/assessment/SnackbarContext";
import LoaderContext from "../../../context/assessment/LoaderContext";

// Helper
import errorHandler from "../../../helper/assessment/errorHandler";
import logRender from "../../../helper/assessment/logRender";

// Controller
import Questionary from "../../../controller/assessment/assessment_system/questionary";

// Component
import TableFilter from "../../../components/assessment/assessment_system/TableFilter";
import Table from "../../../components/assessment/assessment_system/Table";

function List() {
  const { setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  const [list, setList] = useState<Array<TQuestionary & TCategory>>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    logRender({ type: "page", name: "questionary/List" });
  }, []);

  const getList = useCallback(async () => {
    try {
      setMessage("Fetch Questionary");
      const qc = new Questionary();
      const res = await qc.list();
      setList(res.data.list);
      setMessage("");
    } catch (error) {
      setMessage("");
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  }, [setNotif, setMessage]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleDeleteList = (id: number) => {
    const arrFilter = [...list].filter((v) => v.id !== id);
    setList(arrFilter);
  };

  return (
    <div className="w-full flex flex-col space-y-0.5">
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <Link
          to={`create`}
          className="block w-auto mb-2 md:m-0 px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
        >
          <div className="flex">
            <PencilIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
            Create
          </div>
        </Link>
        <div className="w-full md:w-auto ml-auto flex">
          <TableFilter search={setSearch} />
        </div>
      </div>
      <div>
        <QuestionaryList list={list} handleDeleteList={handleDeleteList} search={search} />
      </div>
    </div>
  );
}

function QuestionaryList(props: { list: Array<TQuestionary & TCategory>; handleDeleteList: (id: number) => void; search: string }) {
  const setNotif = useContext(SnackbarContext).setNotif;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const confirmDelete = useCallback(
    async (questionary: TQuestionary & TCategory) => {
      try {
        const confirm = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });
        if (confirm.isConfirmed) {
          const qc = new Questionary();
          await qc.delete(questionary);
          props.handleDeleteList(questionary.id);
          setNotif({ type: "success", message: "questionary deleted" });
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [props, setNotif]
  );

  const columns = useMemo(
    () => [
      {
        id: "mobile",
        Header: "Questionary",
        accessor: "id",
        show: isMobile,
        Cell: (props: { row: { original: TQuestionary & TCategory } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.description || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "description",
        classHeader: "text-left px-2",
        Header: "Description",
        classDivHeader: "flex items-center",
        accessor: "description",
        show: !isMobile,
        Cell: (props: { row: { original: TQuestionary & TCategory } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.description || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "category",
        classHeader: "text-left px-2",
        Header: "Category",
        classDivHeader: "flex items-center",
        accessor: "category",
        show: !isMobile,
        Cell: (props: { row: { original: TQuestionary & TCategory } }) => {
          return <span>{props.row.original.category || "NULL"}</span>;
        },
      },
      {
        id: "time_limit",
        classHeader: "text-left px-2",
        Header: "Time Limit",
        classDivHeader: "flex items-center",
        accessor: "time_limit",
        show: !isMobile,
        Cell: (props: { row: { original: TQuestionary & TCategory } }) => {
          return <span>{props.row.original.time_limit || "NULL"}</span>;
        },
      },
      {
        id: "file_path",
        Header: "PDF",
        classDivHeader: "flex justify-center",
        accessor: "file_path",
        show: !isMobile,
        disableSortBy: true,
        Cell: (props: { row: { original: TQuestionary & TCategory } }) => {
          return (
            <Link
              to={`/upload/assessment_system/get/${props.row.original.file_path}`}
              target="_blank"
              className="flex justify-center text-blue-500 hover:text-blue-800"
            >
              <DocumentTextIcon className="h-5" />
            </Link>
          );
        },
      },
      {
        id: "action_delete",
        classHeader: "w-[30px] text-white",
        Header: () => {
          return <TrashIcon className="h-5" />;
        },
        classDivHeader: "flex justify-center",
        accessor: "id",
        show: !isMobile,
        disableSortBy: true,
        className: "text-white bg-red-500 hover:bg-red-800",
        Cell: (props: { row: { original: TQuestionary & TCategory } }) => {
          return (
            <button className="flex justify-center" onClick={() => confirmDelete(props.row.original)}>
              <TrashIcon className="h-5" />
            </button>
          );
        },
      },
    ],
    [isMobile, confirmDelete]
  );

  return <Table columns={columns} list={props.list} search={props.search} />
}

export default List;
